import type { Festgehalten } from '@netcon-work/shared'
import type { StromTermin } from './strom'

/**
 * **Wohin ein festgehaltener Eintrag gehört** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md)
 * AP 24.10) – in dieser Reihenfolge:
 *
 * 1. der Termin, der gerade läuft,
 * 2. die Aufgabe im Puls,
 * 3. der nächste Termin heute, in dem eine genannte Person sitzt (`@Fabian`),
 * 4. sonst ein Zettel – die Einordnung des Notizbuchs legt ihn später ab.
 *
 * Bei einer **Frage** (`?`) steht der Termin mit der Person vorn: Fragen sind für die nächste Runde.
 * Die erste Stelle ist der Vorschlag; ⇥ geht reihum durch alle.
 */
export type Ziel =
  | { art: 'termin'; termin: StromTermin; grund: string }
  | { art: 'aufgabe'; id: string; titel: string; grund: string }
  | { art: 'zettel'; grund: string }

/** Sitzt jemand mit diesem Namen im Termin? Vorname oder Nachname, Anfang genügt. */
export function sitztDrin(termin: StromTermin, name: string): boolean {
  const n = name.toLowerCase()
  return (termin.teilnehmer ?? []).some((t) =>
    t
      .toLowerCase()
      .split(/[\s.@_-]+/)
      .some((teil) => teil.length > 1 && teil.startsWith(n)),
  )
}

export function zieleBestimmen({
  gelesen,
  laufend,
  puls,
  kommende,
}: {
  gelesen: Pick<Festgehalten, 'art' | 'personen'>
  laufend: StromTermin | null
  puls: { id: string; titel: string } | null
  /** Termine, die heute noch beginnen – ohne den laufenden. */
  kommende: readonly StromTermin[]
}): Ziel[] {
  const ziele: Ziel[] = []
  if (laufend) ziele.push({ art: 'termin', termin: laufend, grund: 'läuft' })
  if (puls) ziele.push({ art: 'aufgabe', id: puls.id, titel: puls.titel, grund: 'im Puls' })

  const mitPerson: Ziel[] = []
  for (const name of gelesen.personen) {
    const t = kommende.find((k) => k.id !== laufend?.id && sitztDrin(k, name))
    if (t && !mitPerson.some((z) => z.art === 'termin' && z.termin.id === t.id)) {
      mitPerson.push({ art: 'termin', termin: t, grund: `${name} ist dabei` })
    }
  }
  if (gelesen.art === 'frage') ziele.unshift(...mitPerson)
  else ziele.push(...mitPerson)

  ziele.push({ art: 'zettel', grund: 'später ablegen' })
  return ziele
}

/** `2026-10-23T13:30:00` – die Form, in der das Notizbuch Termine abschreibt. */
export function terminKurz(t: StromTermin) {
  return {
    titel: t.betreff,
    beginn: t.beginn.slice(0, 19),
    ende: t.ende.slice(0, 19),
    ...(t.teilnehmer?.length ? { teilnehmer: [...t.teilnehmer] } : {}),
    ...(t.serie ? { serie: t.serie } : {}),
  }
}
